import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ProjectActivityResponse, ProjectAliasDto, ProjectDetail, ProjectMeetingsResponse, ProjectRefDto, ProjectShareDto, ProjectSlackResponse, SharedTodosResponse } from '../../shared/api-types.js';
import { PROJECT_SET_BY_LABELS, isOneOf } from '../../shared/enums.js';
import { api, queryString } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import { RegionErrorBoundary } from '../shell/RegionErrorBoundary.js';
import {
  Button,
  Chip,
  ConfirmDialog,
  CopyButton,
  EM_DASH,
  FailureView,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  Page,
  PageHead,
  ProjectChip,
  RelativeTime,
  StateView,
  StatusPill,
  TabPanel,
  Tabs,
  failureDetail,
  formatCount,
  formatList,
  formatDateTime,
  inlineFailureText,
  pluralise,
  projectChipSlot,
  useIsMobile,
  useRequest,
  useToast,
  useUrlSort,
  useUrlTab,
  type Crumb,
  type DataTableStatus,
  type MenuEntry,
} from '../ui/index.js';
import {
  INACTIVE_SHARE_NOTHING_CROSSES,
  PROJECT_TABS,
  colourSlotWords,
  forgetAbsorbedTexts,
  forgetNameLabel,
  groupedHereWords,
  leaveShareTexts,
  mergedIntoToast,
  ownTabCount,
  partyInSentence,
  projectApiPath,
  projectDigest,
  projectSharePath,
  projectStatePill,
  shareActPath,
  stopSharingTexts,
  undoRenameRequest,
  type ProjectTabId,
} from './lib/projects.js';
import { slackAnalysisFloorMet } from './lib/slack-context.js';
import { parseTodoFilters, writeTodoFilters, type TodoFilters } from './lib/todos.js';
import { useLazyRequest } from './lib/use-lazy-request.js';
import { DeleteProjectDialog, MergeInDialog, MergeProjectDialog, RenameProjectDialog, ShareProjectDialog } from './project-dialogs.js';
import { OwnMeetingsTable, PROJECT_MEETINGS_SORT_KEYS, ProjectActivityTab, ProjectSlackTab, SharedByPanel, SharedMeetingsTable, SharedSlackNote, SharedTodosTable, SharesTable, type ProjectMeetingsSortKey } from './project-tabs.js';
import { AddTodoDialog, TodoFilterBar, TodoTable, useTodoList } from './todo-table.js';
import './pages.css';

// The project record page (design §7.3; 06-modules §5 in full). R1 — the identity header — is never lazy: the name
// beside its colour swatch (the word travels with the colour, 01-foundations §1.8), at most one state pill, the
// quotable id, the provenance and the primary act. R2 is the field list behind a digest on a phone. R3 are the tabs —
// to-dos, meetings, Slack, people, activity — each loaded when first opened or deep-linked, every embedded table
// sorting with its sort in the URL under its own namespace. R4 is the audit store read at record grain. An absorbed
// id lands on the survivor and says so, with the merge's Undo while it stands. A project shared with the reader shows
// S1's facts alone: what crosses is read live from the sharer's rows, never copied, never their Slack material.

const TODOS_PREFIX = 'todos_';
const TODO_HIGHLIGHT_PARAM = 'todo';
const MEETINGS_SERVER_SORT_KEYS = ['started_at', 'title', 'todo_count'] as const;
const CRUMBS: readonly Crumb[] = [{ label: 'Projects', to: PATHS.projects, role: 'module' }];

/** What the absorbed page hands the survivor's through the router's state: who merged where, so the survivor toasts it once. */
interface MergedFromState {
  mergedFrom?: { id: string; name: string };
}

type DialogState =
  | { kind: 'rename' }
  | { kind: 'merge'; into: ProjectRefDto | null }
  | { kind: 'share' }
  | { kind: 'delete' }
  | { kind: 'merge-in' }
  | null;

type ConfirmState =
  | { kind: 'stop-sharing'; share: ProjectShareDto }
  | { kind: 'leave' }
  | { kind: 'forget-absorbed'; alias: ProjectAliasDto }
  | null;

/**
 * The person's own to-dos of the project: the one to-do table, narrowed to the project (its filters under `todos_`),
 * with the collection's own way to add one — a new to-do is filed under this project as it is made and the row is
 * brought into view (06-modules §5.6: creating a related record belongs in the region it concerns).
 */
function OwnTodosPanel({ project, onMutated }: { project: ProjectDetail; onMutated: () => void }) {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const todoFilters = useMemo(() => parseTodoFilters(params, TODOS_PREFIX), [params]);
  const setTodoFilters = useCallback((next: TodoFilters) => {
    setParams((current) => writeTodoFilters(current, next, TODOS_PREFIX), { replace: true });
  }, [setParams]);
  const todos = useTodoList(todoFilters, { prefix: TODOS_PREFIX, fixedProjectId: project.id });
  const total = project.counts?.todos ?? null;
  const addButton = (size: 'sm' | 'md') => <Button variant="secondary" icon="plus" size={size} onClick={() => setAddOpen(true)}>Add a to-do</Button>;
  return (
    <div className="stack">
      <div className="split">
        <p className="text-sm secondary">
          {project.counts ? `${formatCount(project.counts.openTodos)} open · ${pluralise(project.counts.todos, { one: '# to-do', other: '# to-dos' })} in all` : 'To-dos filed under this project'}
        </p>
        {addButton('sm')}
      </div>
      <TodoFilterBar
        filters={todoFilters}
        onChange={setTodoFilters}
        hideMeetingFilter
        hideProjectFilter
        counts={todos.request.data && total !== null ? { matching: todos.request.data.matching, total } : null}
      />
      <TodoTable
        caption="To-dos in this project"
        list={todos}
        filters={todoFilters}
        onFiltersChange={setTodoFilters}
        prefix={TODOS_PREFIX}
        highlightParam={TODO_HIGHLIGHT_PARAM}
        fixedProjectId={project.id}
        {...(total !== null ? { totalInSet: total } : {})}
        onMutated={onMutated}
        empty={{
          title: 'No to-dos in this project yet',
          description: 'Calis files a to-do here when it belongs to this project; you can also add one now, or file one from the To-dos page.',
          action: addButton('md'),
        }}
      />
      <AddTodoDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        initialProjectIds={[project.id]}
        onCreated={(created) => {
          setAddOpen(false);
          toast.success('To-do added');
          // The new row, identified: the search cleared so it is not filtered out, and the highlight on it (§5.6).
          setParams((current) => {
            const next = writeTodoFilters(current, { ...todoFilters, q: '' }, TODOS_PREFIX);
            next.set(TODO_HIGHLIGHT_PARAM, created.id);
            return next;
          }, { replace: true });
          todos.request.reload();
          onMutated();
        }}
      />
    </div>
  );
}

export default function ProjectPage() {
  const { id = '' } = useParams<{ id: string }>();
  const me = useMe();
  const toast = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [urlTab, setTab] = useUrlTab<ProjectTabId>(PROJECT_TABS, 'todos');

  const detail = useRequest<ProjectDetail>((signal) => api.get<ProjectDetail>(projectApiPath(id), { signal }), [id]);
  const project = detail.data;
  const shared = project?.kind === 'shared';
  const live = project !== null && project.mergedInto === null;
  // The Slack tab follows the meeting page's rule (owner ruling D8; design-deviations.md §2): absent while no Slack check
  // can exist in this installation; present on a shared project only to say nothing of Slack crosses (S1).
  const slackShown = slackAnalysisFloorMet(me.integrations);
  const tab: ProjectTabId = urlTab === 'slack' && !slackShown ? 'todos' : urlTab;

  // --- An absorbed id: land on the survivor and say so (design §7.3 R1) ----------------------------------------------------
  useEffect(() => {
    if (!project || project.mergedInto === null) return;
    const state: MergedFromState = { mergedFrom: { id: project.id, name: project.name } };
    navigate({ pathname: PATHS.project(project.mergedInto.id), search: location.search, hash: location.hash }, { replace: true, state });
  }, [project, navigate, location.search, location.hash]);

  const undoMerge = useCallback(async (absorbed: { id: string; name: string }): Promise<void> => {
    const result = await api.post<ProjectDetail>(`${projectApiPath(absorbed.id)}/unmerge`);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t undo the merge. {failureDetail(result)}</>);
      return;
    }
    toast.success(<><strong>{absorbed.name}</strong> is a project of its own again.</>);
    navigate(PATHS.project(absorbed.id));
  }, [navigate, toast]);

  const mergedFrom = (location.state as MergedFromState | null)?.mergedFrom ?? null;
  const announcedMerge = useRef<string | null>(null);
  useEffect(() => {
    // Why `project.id !== id` waits: useRequest keeps the previous record on screen while the next loads, so right after
    // the merge dialog navigates here the detail is still the absorbed project's; announcing on it would name the wrong
    // project, find no alias to undo by, and clear the state before the survivor ever arrived.
    if (!project || !live || !mergedFrom || project.id !== id || announcedMerge.current === mergedFrom.id) return;
    announcedMerge.current = mergedFrom.id;
    // Undo while the merge is still undoable — the survivor's alias row for the absorbed project carries its mergeId.
    const alias = project.aliases.find((row) => row.id === mergedFrom.id);
    const undoable = alias?.mergeId !== null && alias?.mergeId !== undefined;
    toast.info(mergedIntoToast(project.name), undoable ? { action: { label: 'Undo merge', onClick: () => void undoMerge(mergedFrom) } } : undefined);
    // Why the state is cleared: a refresh must not toast again, and Back must not re-announce.
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null });
  }, [project, live, mergedFrom, id, toast, undoMerge, navigate, location.pathname, location.search, location.hash]);

  // --- R3/R4: each collection loads when its tab is first opened or deep-linked, then stays loaded ------------------------
  const [wanted, setWanted] = useState<Record<ProjectTabId, boolean>>({ todos: false, meetings: false, slack: false, people: false, activity: false });
  useEffect(() => {
    setWanted((current) => (current[tab] ? current : { ...current, [tab]: true }));
  }, [tab]);

  const [meetingsSort] = useUrlSort<ProjectMeetingsSortKey>({ namespace: 'meetings', keys: PROJECT_MEETINGS_SORT_KEYS, defaultSort: { key: 'started_at', dir: 'desc' } });
  const meetingsServerSort = isOneOf(MEETINGS_SERVER_SORT_KEYS, meetingsSort.key) ? meetingsSort : null;
  const meetings = useLazyRequest<ProjectMeetingsResponse>(
    (signal) => api.get<ProjectMeetingsResponse>(`${projectApiPath(id)}/meetings${queryString({ sort: meetingsServerSort?.key ?? null, dir: meetingsServerSort?.dir ?? null })}`, { signal }),
    [id, meetingsServerSort?.key ?? null, meetingsServerSort?.dir ?? null],
    wanted.meetings && live,
  );
  const sharedTodos = useLazyRequest<SharedTodosResponse>(
    (signal) => api.get<SharedTodosResponse>(`${projectApiPath(id)}/todos`, { signal }),
    [id],
    wanted.todos && live,
  );
  const slack = useLazyRequest<ProjectSlackResponse>(
    (signal) => api.get<ProjectSlackResponse>(`${projectApiPath(id)}/slack`, { signal }),
    [id],
    wanted.slack && live && !shared && slackShown,
  );
  const activity = useLazyRequest<ProjectActivityResponse>(
    (signal) => api.get<ProjectActivityResponse>(`${projectApiPath(id)}/activity`, { signal }),
    [id],
    wanted.activity && live,
  );

  /** After a change to the record: the detail, and whatever collections are loaded, read again (no shared cache, design §7.8). */
  const reloadAll = useCallback(() => {
    detail.reload();
    if (meetings.status !== 'idle') meetings.reload();
    if (sharedTodos.status !== 'idle') sharedTodos.reload();
    if (activity.status !== 'idle') activity.reload();
  }, [detail, meetings, sharedTodos, activity]);

  // --- Dialogs, confirmations and the acts behind them ----------------------------------------------------------------------
  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [busyShareId, setBusyShareId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Why the detail and not the server's message: it says why and what next, a refusal's next step included (`failureDetail`).
  const failed = (what: string, detail: ReactNode): void => {
    toast.error(<>We couldn&rsquo;t {what}. {detail}</>);
  };

  // Why the request is built from the detail held before the rename (`undoRenameRequest`; owner ruling 19 of 2026-10-04:
  // "the toast's Undo restores the former name and forgets the abandoned one, so 'Also known as' is as it was before the
  // rename"): the server cannot tell an Undo from a rename back by hand, nor whether the abandoned name was a former name
  // before the rename — only this page, holding both details, can; the helper says `undo: true` exactly when forgetting
  // puts the list back, and sends a plain rename back otherwise.
  const undoRename = async (before: ProjectDetail, renamed: ProjectDetail): Promise<void> => {
    const result = await api.patch<ProjectDetail>(projectApiPath(renamed.id), undoRenameRequest(before, renamed));
    if (!result.ok) failed('restore the name', failureDetail(result));
    else detail.setData(result.data);
    reloadAll();
  };

  const revokeShare = async (projectId: string, share: ProjectShareDto): Promise<boolean> => {
    setBusyShareId(share.id);
    const result = await api.delete<ProjectDetail>(projectSharePath(projectId, share.id));
    setBusyShareId(null);
    if (!result.ok) {
      failed('end the share', failureDetail(result));
      return false;
    }
    detail.setData(result.data);
    return true;
  };

  const shareAct = async (projectId: string, share: ProjectShareDto, act: 'merge-back' | 'unmerge-back'): Promise<void> => {
    setBusyShareId(share.id);
    const result = await api.post<ProjectDetail>(projectSharePath(projectId, share.id, act));
    setBusyShareId(null);
    if (!result.ok) {
      failed(act === 'merge-back' ? 'merge back' : 'undo the merge-back', failureDetail(result));
      return;
    }
    detail.setData(result.data);
    if (meetings.status !== 'idle') meetings.reload();
    if (sharedTodos.status !== 'idle') sharedTodos.reload();
    if (act === 'merge-back') {
      toast.success(<>Merged back: you now group {share.recipient.displayName}&rsquo;s own meetings and to-dos of this project here.</>, {
        action: { label: 'Undo', onClick: () => void shareAct(projectId, { ...share, mergedBack: true }, 'unmerge-back') },
      });
    } else {
      toast.success(<>No longer grouping {share.recipient.displayName}&rsquo;s own rows here.</>);
    }
  };

  const unmergeIn = async (shareId: string): Promise<void> => {
    setBusy(true);
    const result = await api.post<ProjectDetail>(shareActPath(shareId, 'unmerge-in'));
    setBusy(false);
    if (!result.ok) {
      failed('unmerge it', failureDetail(result));
      return;
    }
    // Why "any merge-back": the sharer may never have merged back; the server withdraws one only where it stood.
    toast.success('Unmerged. It stands on its own again, and any merge-back the sharer made is withdrawn.');
    reloadAll();
  };

  const leave = async (shareId: string, name: string): Promise<void> => {
    setBusy(true);
    const result = await api.post<{ left: true; shareId: string }>(shareActPath(shareId, 'leave'));
    setBusy(false);
    if (!result.ok) {
      setConfirmError(inlineFailureText(result));
      return;
    }
    setConfirm(null);
    toast.success(<>You left <strong>{name}</strong>. Only its sharer can share it with you again.</>);
    navigate(PATHS.projects);
  };

  const forgetAlias = async (alias: ProjectAliasDto): Promise<void> => {
    setBusy(true);
    const result = await api.delete<unknown>(projectApiPath(alias.id));
    setBusy(false);
    if (!result.ok) {
      if (confirm?.kind === 'forget-absorbed') setConfirmError(inlineFailureText(result));
      else failed(`forget the name ${alias.name}`, failureDetail(result));
      return;
    }
    setConfirm(null);
    toast.success(<>Forgot <strong>{alias.name}</strong>.</>);
    reloadAll();
  };

  const unmergeAlias = async (alias: ProjectAliasDto): Promise<void> => {
    await undoMerge({ id: alias.id, name: alias.name });
  };

  // --- Condensed identity while scrolling (06-modules §5.3 R1) -----------------------------------------------------------------
  const headRef = useRef<HTMLDivElement | null>(null);
  const [condensed, setCondensed] = useState(false);
  useEffect(() => {
    const head = headRef.current;
    if (!head || !project) return;
    let frame = 0;
    const update = (): void => {
      frame = 0;
      const bar = document.querySelector<HTMLElement>('.top-bar');
      const barBottom = bar ? bar.getBoundingClientRect().bottom : 0;
      setCondensed(head.getBoundingClientRect().bottom < barBottom);
    };
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [project]);

  // --- Page states ------------------------------------------------------------------------------------------------------------
  if (detail.status === 'loading' && !project) {
    return (
      <Page measure="read-wide" className="record-page">
        <PageHead title="Project" crumbs={CRUMBS} />
        <StateView kind="loading" label="Loading the project" minHeight="40dvh" />
      </Page>
    );
  }

  if (!project || project.mergedInto !== null) {
    const failure = detail.error;
    return (
      <Page measure="read-wide" className="record-page">
        <PageHead title="Project" crumbs={CRUMBS} />
        {failure && failure.code === 'not_found' ? (
          <StateView
            kind="not-found"
            title="Project not found"
            description={
              <>
                There is no project with the id <code className="selectable">{id}</code> among yours, and none is shared with you under it. It may have been deleted, the share may have ended, or the link may be out of date.
              </>
            }
            action={<LinkButton to={PATHS.projects} variant="secondary">Go to projects</LinkButton>}
            minHeight="40dvh"
          />
        ) : failure ? (
          <FailureView failure={failure} what="this project" onRetry={detail.reload} backTo={{ to: PATHS.projects, label: 'Go to projects' }} minHeight="40dvh" />
        ) : (
          <StateView kind="loading" label={project ? `Opening ${project.mergedInto?.name ?? 'the project'}` : 'Loading the project'} minHeight="40dvh" />
        )}
      </Page>
    );
  }

  // --- R1 ------------------------------------------------------------------------------------------------------------------------
  const slot = projectChipSlot(project);
  const pillWord = projectStatePill(project);
  const pill = pillWord ? <StatusPill tone="info" icon="users">{pillWord}</StatusPill> : null;
  const swatch = <span className="project-swatch" data-slot={slot} aria-hidden="true" />;
  const share = project.share;
  const inactiveShare = share?.state === 'inactive';
  const sharerName = share?.sharedBy.displayName ?? project.sharedBy?.displayName ?? 'A colleague';
  // Why the server's flag (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere): a sharer
  // no longer admitted reads so on the subtitle and the People tab, and those words are never spliced in as a name.
  const sharerWithheld = (share?.sharedBy ?? project.sharedBy)?.withheld === true;

  const ownMenu: MenuEntry[] = [
    { id: 'merge', label: 'Merge into…', icon: 'link', onSelect: () => setDialog({ kind: 'merge', into: null }) },
    { id: 'share', label: 'Share…', icon: 'users', onSelect: () => setDialog({ kind: 'share' }) },
    { id: 'delete', label: 'Delete', icon: 'trash', danger: true, onSelect: () => setDialog({ kind: 'delete' }) },
  ];
  // Why Leave lives in the overflow, as Delete does on an own project: it is irreversible for the reader (only the
  // sharer can share it again), and a destructive control is never adjacent to the primary (06-modules §5.6, §5.10;
  // 03-components §2) — stacked full-width on a phone the two sat eight pixels apart.
  const sharedMenu: MenuEntry[] = [
    { id: 'leave', label: 'Leave', icon: 'x', danger: true, onSelect: () => { setConfirmError(null); setConfirm({ kind: 'leave' }); } },
  ];
  // Why `secondary` for the overflow trigger: on a phone the head's actions stack and a ghost icon-only button has no
  // box of its own — a lone "···" read as a decoration, not the one way to Merge into…, Share… and Delete.
  const overflow = (entries: MenuEntry[]) => (
    <Menu label={`More actions for ${project.name}`} entries={entries} trigger={(props) => <IconButton {...props} icon="more-horizontal" label="More actions" variant="secondary" />} />
  );
  const headActions = shared ? (
    <>
      {!inactiveShare && share && share.mergedInto === null ? <Button variant="primary" icon="link" onClick={() => setDialog({ kind: 'merge-in' })} disabled={busy}>Merge into one of mine</Button> : null}
      {!inactiveShare && share && share.mergedInto !== null ? <Button variant="primary" icon="unlink" onClick={() => void unmergeIn(share.id)} loading={busy}>Unmerge</Button> : null}
      {overflow(sharedMenu)}
    </>
  ) : (
    <>
      <Button variant="primary" icon="edit" onClick={() => setDialog({ kind: 'rename' })}>Rename</Button>
      {overflow(ownMenu)}
    </>
  );

  // Why `partyInSentence` (the owner's ruling of 2026-10-11, "A colleague (no longer has access)" everywhere): after "Shared
  // by" a withheld sharer reads as the digest and the history write it, "a colleague (no longer has access)".
  const subtitle: ReactNode = shared
    ? <>Shared by {partyInSentence({ displayName: sharerName, withheld: sharerWithheld })} · since <RelativeTime value={project.createdAt} /></>
    : <>{project.namedBy ? PROJECT_SET_BY_LABELS[project.namedBy] : 'Named'} · created <RelativeTime value={project.createdAt} />{detail.refreshing ? <span className="visually-hidden"> (checking for changes)</span> : null}</>;

  // --- R3 counts: undefined (no badge) until that collection is known — never a 0 before the data (06-modules §5.3) ----------
  const sharedGroupCount = (groups: ReadonlyArray<{ items: readonly unknown[] }> | undefined): number | undefined => (groups ? groups.reduce((sum, group) => sum + group.items.length, 0) : undefined);
  // Why the own page adds the groups (§5.3 R3, "as honest about its extent"): its To-dos and Meetings tabs show the own
  // rows AND every merged-in or merged-back group beneath them, so a badge of the own count alone read "0" above seven
  // shared rows. The own badge counts open to-dos, so the shared rows counted beside it are the open ones too.
  const expectsGroups = project.mergedInShares.length > 0 || project.mergedBackShares.length > 0;
  const sharedOpenCount = sharedTodos.data ? sharedTodos.data.groups.reduce((sum, group) => sum + group.items.filter((todo) => todo.status === 'open').length, 0) : undefined;
  const todosCount = shared ? sharedGroupCount(sharedTodos.data?.groups) : ownTabCount(project.counts?.openTodos, expectsGroups, sharedOpenCount);
  const meetingsCount = shared ? sharedGroupCount(meetings.data?.shared) : ownTabCount(project.counts?.meetings, expectsGroups, sharedGroupCount(meetings.data?.shared));
  const slackCount = !shared && slack.data ? slack.data.findings.length + slack.data.matters.length : undefined;
  const peopleCount = shared ? undefined : project.shares.length;
  const tabs = [
    { id: 'todos' as const, label: 'To-dos', count: todosCount },
    { id: 'meetings' as const, label: 'Meetings', count: meetingsCount },
    ...(slackShown ? [{ id: 'slack' as const, label: 'Slack', count: slackCount }] : []),
    { id: 'people' as const, label: 'People', count: peopleCount },
    // Why no count on a shared project's tab (the owner's ruling D32): its list is the reader's own acts, and a count on
    // the region's name would read as the project's history, which is withheld.
    { id: 'activity' as const, label: 'Activity', count: activity.data?.view === 'record' ? activity.data.items.length : undefined },
  ];

  const mergedInNames = new Map(project.mergedInShares.map((entry) => [entry.shareId, entry.projectName]));
  const groupStatus = (status: 'idle' | 'loading' | 'ready' | 'error'): DataTableStatus => (status === 'ready' ? { kind: 'ready' } : { kind: 'loading', rowCount: 4 });
  const meetingsStatus: DataTableStatus = meetings.status === 'error' && meetings.error
    ? { kind: 'error', failure: meetings.error, onRetry: meetings.reload }
    : groupStatus(meetings.status);

  // --- R2 rows ---------------------------------------------------------------------------------------------------------------------
  const aliasRows = project.aliases.map((alias) => (
    <li key={alias.id} className="project-alias">
      <Chip
        tone="neutral"
        icon={alias.absorbed ? 'link' : undefined}
        title={alias.absorbed ? `A project merged into ${project.name}` : `A former name of ${project.name}`}
        onRemove={() => {
          if (alias.absorbed) {
            setConfirmError(null);
            setConfirm({ kind: 'forget-absorbed', alias });
          } else void forgetAlias(alias);
        }}
        removeLabel={forgetNameLabel(alias.name)}
      >
        {alias.name}
      </Chip>
      {alias.absorbed && alias.mergeId ? <Button size="sm" variant="link" icon="undo" onClick={() => void unmergeAlias(alias)}>Unmerge</Button> : null}
    </li>
  ));

  return (
    <Page measure="read-wide" className="record-page project-page">
      <div ref={headRef}>
        <PageHead
          title={project.name}
          crumbs={CRUMBS}
          titleAside={<>{swatch}{pill}</>}
          identifier={
            <span className="record-identifier">
              <span>Project</span>
              <code className="selectable">{project.id.slice(0, 8)}</code>
              <CopyButton value={project.id} label="Copy id" />
            </span>
          }
          subtitle={subtitle}
          actions={headActions}
        />
      </div>

      <div className="record-sticky" hidden={!condensed} aria-hidden="true">
        <span className="record-sticky__kind">Project</span>
        <span className="record-sticky__title">{swatch} {project.name}</span>
        {pill ? <span className="record-sticky__pill">{pill}</span> : null}
      </div>

      {/* R2. On a phone the attributes collapse behind a one-line digest so the record's own content begins within
          the first viewport (06-modules §5.10); the identifying fields stay in the header above. On a shared project
          the sharer's provenance, counts and former names are not the reader's to see and are absent, not dashed (§5.7). */}
      <details className="disclosure record-details" open={!isMobile} key={isMobile ? 'mobile' : 'desktop'}>
        <summary>
          <Icon name="chevron-down" size={16} />
          <span className="record-details__label">Details</span>
          <span className="record-details__digest text-sm secondary num">{projectDigest(project)}</span>
        </summary>
        <div className="disclosure__body">
          <dl className="field-list">
            {shared ? (
              <>
                <dt>Shared by</dt>
                <dd>{sharerName}</dd>
                <dt>Since</dt>
                <dd><RelativeTime value={project.createdAt} mode="both" /></dd>
                <dt>Grouped under</dt>
                <dd>{share?.mergedInto ? <ProjectChip project={share.mergedInto} /> : <span className="muted">{EM_DASH} not grouped with a project of yours</span>}</dd>
              </>
            ) : (
              <>
                <dt>Named by</dt>
                <dd>{project.namedBy === 'ai' ? <span className="project-named-by"><Icon name="sparkles" size={14} /> Calis</span> : 'You'}</dd>
                <dt>AI’s name</dt>
                <dd>{project.aiName ?? <span className="muted">{EM_DASH}</span>}</dd>
                <dt>Created</dt>
                <dd><time dateTime={project.createdAt}>{formatDateTime(project.createdAt)}</time></dd>
                <dt>Last used</dt>
                <dd>{project.lastUsedAt ? <RelativeTime value={project.lastUsedAt} mode="both" /> : <span className="muted">{EM_DASH}</span>}</dd>
                <dt>Also known as</dt>
                <dd>
                  {aliasRows.length > 0 ? (
                    <div className="stack stack--1">
                      <ul className="cluster project-aliases" role="list" aria-label="Former names and merged projects">{aliasRows}</ul>
                      <p className="text-xs muted">A former name still finds this project; remove one with its ×. A merged project can be unmerged while its merge stands.</p>
                    </div>
                  ) : <span className="muted">{EM_DASH}</span>}
                </dd>
                <dt>Shared with</dt>
                <dd>
                  {project.shares.length > 0
                    ? formatList(project.shares.filter((row) => row.state !== 'left' && row.state !== 'revoked').map((row) => row.recipient.displayName)) || <span className="muted">{EM_DASH}</span>
                    : <span className="muted">{EM_DASH}</span>}
                </dd>
                <dt>Grouped here</dt>
                <dd>
                  {project.mergedInShares.length > 0
                    // Why `formatList` (05-content §8; the Phase 1c audit's D30): each phrase is one whole template, and the list is
                    // joined as the locale joins one — never with ", " by hand.
                    ? formatList(project.mergedInShares.map(groupedHereWords))
                    : <span className="muted">{EM_DASH}</span>}
                </dd>
              </>
            )}
            <dt>Colour</dt>
            <dd><span className="project-colour">{swatch} {colourSlotWords(slot)}</span></dd>
          </dl>
        </div>
      </details>

      <div className="record-tabs">
        <Tabs<ProjectTabId> id="project" label="Project sections" tabs={tabs} value={tab} onChange={setTab} />

        <TabPanel tabsId="project" tabId="todos" active={tab === 'todos'} heading="To-dos">
          <RegionErrorBoundary region="the to-dos" compact>
            <div className="stack stack--5">
              {!shared ? <OwnTodosPanel project={project} onMutated={detail.reload} /> : null}
              {sharedTodos.status === 'error' && sharedTodos.error ? (
                <FailureView failure={sharedTodos.error} what="the shared to-dos" onRetry={sharedTodos.reload} compact />
              ) : sharedTodos.data ? (
                sharedTodos.data.groups.length === 0 && shared
                  ? <StateView kind="empty" title="No to-dos here" description={inactiveShare ? INACTIVE_SHARE_NOTHING_CROSSES : `${sharerName} has no to-dos in this project that can cross on a share.`} minHeight="10rem" />
                  : sharedTodos.data.groups.map((group) => <SharedTodosTable key={group.shareId} group={group} projectNames={mergedInNames} />)
              ) : shared ? (
                <StateView kind="loading" label="Loading the shared to-dos" minHeight="12rem" />
              ) : null}
            </div>
          </RegionErrorBoundary>
        </TabPanel>

        <TabPanel tabsId="project" tabId="meetings" active={tab === 'meetings'} heading="Meetings">
          <RegionErrorBoundary region="the meetings" compact>
            <div className="stack stack--5">
              {!shared ? <OwnMeetingsTable rows={meetings.data?.items ?? []} status={meetingsStatus} total={meetings.data?.total ?? null} /> : null}
              {meetings.status === 'error' && meetings.error && shared ? (
                <FailureView failure={meetings.error} what="the shared meetings" onRetry={meetings.reload} compact />
              ) : meetings.data ? (
                meetings.data.shared.length === 0 && shared
                  ? <StateView kind="empty" title="No meetings here" description={inactiveShare ? INACTIVE_SHARE_NOTHING_CROSSES : `${sharerName} has no meetings in this project yet.`} minHeight="10rem" />
                  : meetings.data.shared.map((group) => <SharedMeetingsTable key={group.shareId} group={group} />)
              ) : shared ? (
                <StateView kind="loading" label="Loading the shared meetings" minHeight="12rem" />
              ) : null}
            </div>
          </RegionErrorBoundary>
        </TabPanel>

        {slackShown ? (
          <TabPanel tabsId="project" tabId="slack" active={tab === 'slack'} heading="Slack">
            <RegionErrorBoundary region="the Slack findings" compact>
              {shared ? <SharedSlackNote /> : <ProjectSlackTab request={slack} sweepOn={me.integrations.slackSweep !== 'off'} />}
            </RegionErrorBoundary>
          </TabPanel>
        ) : null}

        <TabPanel tabsId="project" tabId="people" active={tab === 'people'} heading="People">
          <RegionErrorBoundary region="the people" compact>
            {shared ? (
              <SharedByPanel
                project={project}
                busy={busy}
                onMergeIn={() => setDialog({ kind: 'merge-in' })}
                onUnmergeIn={() => { if (share) void unmergeIn(share.id); }}
                onLeave={() => { setConfirmError(null); setConfirm({ kind: 'leave' }); }}
              />
            ) : (
              <SharesTable
                project={project}
                busyShareId={busyShareId}
                onShare={() => setDialog({ kind: 'share' })}
                onStopSharing={(row) => { setConfirmError(null); setConfirm({ kind: 'stop-sharing', share: row }); }}
                onMergeBack={(row) => void shareAct(project.id, row, 'merge-back')}
                onUnmergeBack={(row) => void shareAct(project.id, row, 'unmerge-back')}
              />
            )}
          </RegionErrorBoundary>
        </TabPanel>

        <TabPanel tabsId="project" tabId="activity" active={tab === 'activity'} heading="Activity">
          <RegionErrorBoundary region="the activity" compact>
            <ProjectActivityTab request={activity} project={project} shared={shared} sharerName={shared && !sharerWithheld ? sharerName : null} />
          </RegionErrorBoundary>
        </TabPanel>
      </div>

      {/* Dialogs (design §7.4) */}
      <RenameProjectDialog
        project={dialog?.kind === 'rename' ? project : null}
        onClose={() => setDialog(null)}
        onRenamed={(renamed) => {
          const before = project; // the detail held before the rename: what the Undo is built from
          setDialog(null);
          detail.setData(renamed);
          toast.success(<>Project renamed to <strong>{renamed.name}</strong>.</>, before ? { action: { label: 'Undo', onClick: () => void undoRename(before, renamed) } } : undefined);
        }}
        onMergeInstead={(into) => setDialog({ kind: 'merge', into })}
      />
      <MergeProjectDialog
        project={dialog?.kind === 'merge' ? project : null}
        initialInto={dialog?.kind === 'merge' ? dialog.into : null}
        onClose={() => setDialog(null)}
        onMerged={(response, absorbed) => {
          setDialog(null);
          // The absorbed page is this one: land on the survivor, which toasts "Merged into X" with the Undo.
          const state: MergedFromState = { mergedFrom: { id: absorbed.id, name: absorbed.name } };
          navigate(PATHS.project(response.project.id), { replace: true, state });
        }}
      />
      <ShareProjectDialog
        executiveOn={me.integrations.executive === 'on'}
        project={dialog?.kind === 'share' ? project : null}
        onClose={() => setDialog(null)}
        onShared={(updated, recipient) => {
          setDialog(null);
          detail.setData(updated);
          const created = updated.shares.find((row) => row.recipient.id === recipient.id && row.state !== 'revoked' && row.state !== 'left');
          toast.success(<>Shared with {recipient.label}. They see this project’s meetings and to-dos from now on.</>, created ? { action: { label: 'Undo', onClick: () => void revokeShare(updated.id, created) } } : undefined);
          if (tab !== 'people') setTab('people');
        }}
      />
      <DeleteProjectDialog
        project={dialog?.kind === 'delete' ? project : null}
        onClose={() => setDialog(null)}
        onDeleted={(deleted) => {
          setDialog(null);
          toast.success(<>Deleted <strong>{deleted.name}</strong>. Its meetings and to-dos keep everything but the label.</>);
          navigate(PATHS.projects, { replace: true });
        }}
      />
      <MergeInDialog
        share={dialog?.kind === 'merge-in' && share ? { id: share.id, projectName: project.name, sharedBy: share.sharedBy } : null}
        onClose={() => setDialog(null)}
        onMergedIn={(into) => {
          setDialog(null);
          toast.success(<>Merged into <strong>{into.name}</strong>: its meetings and to-dos now show there beside yours.</>, { action: { label: 'Undo', onClick: () => { if (share) void unmergeIn(share.id); } } });
          reloadAll();
        }}
      />

      {/* Confirmations: no Undo exists for the other party (03-components §4.2) */}
      <ConfirmDialog
        open={confirm?.kind === 'stop-sharing'}
        // Why the server's flag (the owner's ruling of 2026-10-11): Stop sharing is offered on an inactive share, whose
        // recipient may be withheld, and the title and the toast then say "with a colleague (no longer has access)".
        title={confirm?.kind === 'stop-sharing' ? stopSharingTexts(project.name, confirm.share.recipient.displayName, confirm.share.recipient.withheld === true).title : ''}
        confirmLabel={stopSharingTexts('', '').confirm}
        danger
        busy={busyShareId !== null}
        error={confirmError}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (confirm?.kind !== 'stop-sharing') return;
          const ok = await revokeShare(project.id, confirm.share);
          if (ok) {
            setConfirm(null);
            toast.success(stopSharingTexts(project.name, confirm.share.recipient.displayName, confirm.share.recipient.withheld === true).done);
          }
        }}
      >
        <p>{stopSharingTexts('', '').body}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === 'leave'}
        title={leaveShareTexts(sharerName, project.name, sharerWithheld).title}
        confirmLabel={leaveShareTexts(sharerName, project.name, sharerWithheld).confirm}
        danger
        busy={busy}
        error={confirmError}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { if (share) void leave(share.id, project.name); }}
      >
        <p>{leaveShareTexts(sharerName, project.name, sharerWithheld).body}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === 'forget-absorbed'}
        title={confirm?.kind === 'forget-absorbed' ? forgetAbsorbedTexts(confirm.alias.name, project.name).title : ''}
        confirmLabel={forgetAbsorbedTexts('', '').confirm}
        danger
        busy={busy}
        error={confirmError}
        onCancel={() => setConfirm(null)}
        onConfirm={() => { if (confirm?.kind === 'forget-absorbed') void forgetAlias(confirm.alias); }}
      >
        <p>{confirm?.kind === 'forget-absorbed' ? forgetAbsorbedTexts(confirm.alias.name, project.name).body : ''}</p>
      </ConfirmDialog>
    </Page>
  );
}
